import React, { useRef } from 'react';
import html2pdf from 'html2pdf.js';
import './BonTraitement.css';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

const BonTraitement = ({ personne, type, cotisations = [], onClose }) => {
  const printRef = useRef(null);

  const handlePrint = () => {
    if (!printRef.current) return;
    
    // Créer une nouvelle fenêtre avec le contenu à imprimer
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Veuillez autoriser les pop-ups pour imprimer');
      return;
    }

    const printContent = printRef.current.innerHTML;
    
    // Récupérer les URLs des images pour les convertir en base64 si nécessaire
    const images = printRef.current.querySelectorAll('img');
    const imagePromises = Array.from(images).map(img => {
      return new Promise((resolve) => {
        if (img.src.startsWith('data:') || img.src.startsWith('blob:')) {
          resolve(img.src);
        } else {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const imgElement = new Image();
          imgElement.crossOrigin = 'anonymous';
          imgElement.onload = () => {
            canvas.width = imgElement.width;
            canvas.height = imgElement.height;
            ctx.drawImage(imgElement, 0, 0);
            resolve(canvas.toDataURL('image/png'));
          };
          imgElement.onerror = () => resolve(img.src);
          imgElement.src = img.src;
        }
      });
    });

    Promise.all(imagePromises).then(imageDataUrls => {
      let processedContent = printContent;
      images.forEach((img, index) => {
        if (imageDataUrls[index]) {
          processedContent = processedContent.replace(img.src, imageDataUrls[index]);
        }
      });
    
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Bon de Traitement</title>
            <meta charset="UTF-8">
            <style>
              @page {
                size: A4;
                margin: 0;
              }
              * {
                box-sizing: border-box;
                margin: 0;
                padding: 0;
              }
              body {
                margin: 0;
                padding: 0;
                background: white;
                font-family: Arial, sans-serif;
                color: black;
              }
              .bon-traitement-document {
                padding: 10mm;
                max-width: 100%;
                width: 100%;
                box-sizing: border-box;
                position: relative;
                background: white;
              }
              .bon-traitement-document::before {
                content: 'MUNDES ASBL';
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%) rotate(-45deg);
                font-size: 100px;
                font-weight: bold;
                color: rgba(0, 0, 0, 0.08);
                z-index: 0;
                pointer-events: none;
                white-space: nowrap;
                opacity: 1;
                font-family: Arial, sans-serif;
              }
              .bon-traitement-document > * {
                position: relative;
                z-index: 1;
              }
              .bon-traitement-header-doc {
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 15px;
                margin-bottom: 15px;
                padding-bottom: 10px;
                border-bottom: 2px solid #2c3e50;
                position: relative;
                min-height: 100px;
                z-index: 1;
              }
              .bon-traitement-logo img {
                width: 60px;
                height: 60px;
                object-fit: contain;
              }
              .bon-traitement-header-photo-img {
                width: 80px;
                height: 100px;
                object-fit: cover;
                border: 2px solid #ddd;
                border-radius: 4px;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
              }
              .bon-traitement-title {
                text-align: center;
                flex: 1;
                display: flex;
                flex-direction: column;
                align-items: center;
              }
              .bon-traitement-title h1 {
                margin: 0;
                font-size: 22px;
                color: #2c3e50;
                font-weight: bold;
              }
              .bon-traitement-title h2 {
                margin: 3px 0 0 0;
                font-size: 16px;
                color: #667eea;
                font-weight: 600;
              }
              .bon-traitement-section {
                margin-bottom: 15px;
                position: relative;
                z-index: 1;
              }
              .bon-traitement-section h3 {
                font-size: 14px;
                color: #34495e;
                border-bottom: 1px solid #eee;
                padding-bottom: 5px;
                margin-bottom: 10px;
                font-weight: 600;
              }
              .bon-traitement-info-grid {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                gap: 8px 15px;
              }
              .info-row {
                display: flex;
                flex-direction: column;
                padding: 2px 0;
              }
              .info-label {
                font-size: 11px;
                color: #7f8c8d;
                font-weight: 500;
                margin-bottom: 2px;
              }
              .info-value {
                font-size: 11px;
                color: #2c3e50;
                font-weight: 600;
              }
              .cotisations-table-container,
              .membres-famille-table-container {
                overflow-x: auto;
                width: 100%;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                margin-top: 10px;
                font-size: 10px;
              }
              th, td {
                border: 1px solid #e0e0e0;
                padding: 5px 4px;
                text-align: center;
                vertical-align: middle;
              }
              th {
                background-color: #f8f8f8;
                font-weight: 600;
                color: #34495e;
              }
              tbody tr:nth-child(even) {
                background-color: #f9f9f9;
              }
              .statut-badge {
                display: inline-block;
                padding: 2px 6px;
                border-radius: 3px;
                font-size: 9px;
                font-weight: bold;
                color: white;
              }
              .statut-payee {
                background-color: #28a745;
              }
              .statut-impayee {
                background-color: #dc3545;
              }
              .semaine-details {
                font-size: 8px;
                line-height: 1.3;
              }
              .bon-traitement-footer {
                display: flex;
                justify-content: space-between;
                align-items: flex-end;
                margin-top: 20px;
                padding-top: 10px;
                border-top: 1px solid #e0e0e0;
                flex-wrap: wrap;
                gap: 10px;
              }
              .signature-line p {
                margin: 0;
                font-size: 12px;
                color: #555;
              }
              .signature-space {
                width: 200px;
                height: 40px;
                border-bottom: 1px solid #333;
                margin-top: 8px;
              }
              .bon-traitement-date {
                text-align: right;
              }
              .bon-traitement-date p {
                margin: 0;
                font-size: 12px;
                color: #555;
                font-weight: 600;
              }
              .bon-traitement-contact-info {
                margin-top: 15px;
                padding-top: 10px;
                border-top: 1px solid #e0e0e0;
                text-align: center;
                position: relative;
                z-index: 1;
              }
              .bon-traitement-contact-info p {
                margin: 0;
                font-size: 10px;
                color: #555;
                font-weight: 500;
                line-height: 1.4;
              }
              .bon-traitement-authorization {
                padding: 12px;
                background: #f8f9fa;
                border-left: 4px solid #667eea;
                border-radius: 4px;
                position: relative;
                z-index: 1;
              }
              .bon-traitement-authorization p {
                margin: 8px 0;
                font-size: 11px;
                color: #2c3e50;
                line-height: 1.5;
                text-align: justify;
              }
              .bon-traitement-authorization p:first-child {
                margin-top: 0;
                font-size: 12px;
                font-weight: 600;
                color: #667eea;
                text-align: center;
                text-transform: uppercase;
                margin-bottom: 10px;
              }
              .bon-traitement-authorization p:last-child {
                margin-bottom: 0;
              }
              img {
                max-width: 100%;
                height: auto;
                display: block;
              }
              .close-button-container {
                position: fixed;
                top: 10px;
                right: 10px;
                z-index: 10000;
                background: rgba(255, 255, 255, 0.95);
                padding: 10px;
                border-radius: 5px;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
              }
              .close-button {
                background: #dc3545;
                color: white;
                border: none;
                padding: 10px 20px;
                border-radius: 5px;
                cursor: pointer;
                font-size: 14px;
                font-weight: 600;
                transition: background 0.3s;
              }
              .close-button:hover {
                background: #c82333;
              }
              @media print {
                .close-button-container {
                  display: none !important;
                }
              }
            </style>
          </head>
          <body>
            <div class="close-button-container">
              <button class="close-button" onclick="window.close()">✕ Fermer</button>
            </div>
            ${processedContent}
          </body>
        </html>
      `);
      
      printWindow.document.close();
      
      // Attendre que le contenu soit chargé puis imprimer
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
        printWindow.onafterprint = () => {
          printWindow.close();
        };
      }, 500);
    });
  };

  const handleDownloadPDF = async () => {
    if (!printRef.current) return;

    try {
      // Attendre un peu pour que le contenu soit complètement rendu
      await new Promise(resolve => setTimeout(resolve, 500));

      const opt = {
        margin: [5, 5, 5, 5],
        filename: `Bon_Traitement_${type === 'membre' ? personne.numero_matricule : personne.numero_famille || 'FAM'}.pdf`,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          width: printRef.current.scrollWidth,
          height: printRef.current.scrollHeight,
          windowWidth: printRef.current.scrollWidth,
          windowHeight: printRef.current.scrollHeight,
          allowTaint: true,
          x: 0,
          y: 0,
          scrollX: 0,
          scrollY: 0,
          onclone: (clonedDoc) => {
            // S'assurer que tous les styles sont visibles dans le clone
            const clonedElement = clonedDoc.querySelector('.bon-traitement-document');
            if (clonedElement) {
              clonedElement.style.padding = '10mm';
              clonedElement.style.maxWidth = '100%';
              clonedElement.style.display = 'block';
              clonedElement.style.visibility = 'visible';
              clonedElement.style.opacity = '1';
            }
            
            // S'assurer que tous les éléments sont visibles
            const allElements = clonedDoc.querySelectorAll('.bon-traitement-section, .info-row, .cotisations-table, .membres-famille-table');
            allElements.forEach(el => {
              el.style.display = '';
              el.style.visibility = 'visible';
              el.style.opacity = '1';
            });
          }
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4',
          orientation: 'portrait',
          compress: true
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };

      await html2pdf().set(opt).from(printRef.current).save();
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      alert('Erreur lors de la génération du PDF. Veuillez réessayer.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit' 
    });
  };

  const getMonthName = (mois) => {
    const months = ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
                    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    return months[mois] || mois;
  };

  // Vérifier si les cotisations ont des semaines (membres) ou sont mensuelles (familles)
  const hasSemaines = cotisations.length > 0 && cotisations[0].semaine !== undefined && cotisations[0].semaine !== null;

  // Trier les cotisations par année, mois, et semaine si disponible (ordre décroissant)
  const sortedCotisations = [...cotisations].sort((a, b) => {
    if (a.annee !== b.annee) return b.annee - a.annee;
    if (a.mois !== b.mois) return b.mois - a.mois;
    if (hasSemaines) {
      return (b.semaine || 0) - (a.semaine || 0);
    }
    return 0;
  });

  // Grouper les cotisations par mois/année
  let cotisationsGrouped;
  if (hasSemaines) {
    // Pour les membres : grouper par mois avec semaines
    const cotisationsParMois = sortedCotisations.reduce((acc, cotisation) => {
      const key = `${cotisation.annee}-${cotisation.mois}`;
      if (!acc[key]) {
        acc[key] = {
          annee: cotisation.annee,
          mois: cotisation.mois,
          semaines: {}
        };
      }
      acc[key].semaines[cotisation.semaine] = cotisation;
      return acc;
    }, {});
    cotisationsGrouped = Object.values(cotisationsParMois).sort((a, b) => {
      if (a.annee !== b.annee) return b.annee - a.annee;
      return b.mois - a.mois;
    });
  } else {
    // Pour les familles : une cotisation par mois
    cotisationsGrouped = sortedCotisations.map(cotisation => ({
      annee: cotisation.annee,
      mois: cotisation.mois,
      cotisation: cotisation
    }));
  }

  // Calculer les statistiques
  const totalCotisations = sortedCotisations.length;
  const cotisationsPayees = sortedCotisations.filter(c => c.statut === 'payee').length;
  const cotisationsImpayees = sortedCotisations.filter(c => c.statut === 'impayee').length;
  const montantTotal = sortedCotisations
    .filter(c => c.statut === 'payee')
    .reduce((sum, c) => sum + parseFloat(c.montant || 0), 0);

  return (
    <div className="bon-traitement-modal-overlay" onClick={onClose}>
      <div className="bon-traitement-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="bon-traitement-header">
          <h2>Bon de Traitement</h2>
          <div className="bon-traitement-actions">
            <button onClick={handlePrint} className="btn btn-primary">
              🖨️ Imprimer
            </button>
            <button onClick={handleDownloadPDF} className="btn btn-secondary">
              📥 Télécharger PDF
            </button>
            <button onClick={onClose} className="btn btn-danger">
              ✕ Fermer
            </button>
          </div>
        </div>

        <div className="bon-traitement-body" ref={printRef}>
          <div className="bon-traitement-document">
            {/* En-tête */}
            <div className="bon-traitement-header-doc">
              <div className="bon-traitement-logo">
                <img src="/logo.png" alt="Logo" />
              </div>
              <div className="bon-traitement-title">
                <h1>MUTU NDE ESPOIR ASBL</h1>
                <h2>BON DE TRAITEMENT</h2>
              </div>
              {type === 'membre' && personne.photo && (
                <div className="bon-traitement-header-photo">
                  <img 
                    src={`${API_BASE_URL}${personne.photo}`} 
                    alt={`${personne.nom} ${personne.prenom}`}
                    className="bon-traitement-header-photo-img"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
              {type === 'famille' && personne.photo_chef && (
                <div className="bon-traitement-header-photo">
                  <img 
                    src={`${API_BASE_URL}${personne.photo_chef}`} 
                    alt={personne.chef_famille_nom}
                    className="bon-traitement-header-photo-img"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>

            {/* Informations de la personne */}
            <div className="bon-traitement-section">
              <h3>INFORMATIONS {type === 'membre' ? 'DU MEMBRE' : 'DE LA FAMILLE'}</h3>
              
              {type === 'membre' ? (
                <div className="bon-traitement-info-grid">
                  <div className="info-row">
                    <span className="info-label">Numéro Matricule:</span>
                    <span className="info-value">{personne.numero_matricule || '-'}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Nom complet:</span>
                    <span className="info-value">{[personne.nom, personne.postnom, personne.prenom].filter(Boolean).join(' ') || '-'}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Naissance:</span>
                    <span className="info-value">
                      {formatDate(personne.date_naissance)}
                      {personne.lieu_naissance && ` à ${personne.lieu_naissance}`}
                      {!personne.date_naissance && !personne.lieu_naissance && '-'}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Téléphone:</span>
                    <span className="info-value">{personne.telephone || '-'}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Profession:</span>
                    <span className="info-value">{personne.profession || '-'}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Adresse:</span>
                    <span className="info-value">
                      {personne.quartier || ''}
                      {personne.quartier && personne.commune && ', '}
                      {personne.commune || ''}
                      {!personne.quartier && !personne.commune && '-'}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Centre de Santé:</span>
                    <span className="info-value">{personne.centre_sante || '-'}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Date d'Adhésion:</span>
                    <span className="info-value">{formatDate(personne.date_adhesion)}</span>
                  </div>
                </div>
              ) : (
                <div className="bon-traitement-info-grid">
                  <div className="info-row">
                    <span className="info-label">Numéro Famille:</span>
                    <span className="info-value">{personne.numero_famille || '-'}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Nom de Famille:</span>
                    <span className="info-value">{personne.nom_famille || '-'}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Chef de Famille:</span>
                    <span className="info-value">{personne.chef_famille_nom || '-'}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Téléphone:</span>
                    <span className="info-value">{personne.telephone || '-'}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Adresse:</span>
                    <span className="info-value">
                      {[personne.adresse, personne.quartier, personne.commune].filter(Boolean).join(', ') || '-'}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Centre de Santé:</span>
                    <span className="info-value">{personne.centre_sante || '-'}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Nombre de Membres:</span>
                    <span className="info-value">{personne.nombre_membres || 0}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Date de Création:</span>
                    <span className="info-value">{formatDate(personne.date_creation)}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Texte d'autorisation */}
            <div className="bon-traitement-section">
              <div className="bon-traitement-authorization">
                <p>
                  <strong>AUTORISATION DE TRAITEMENT</strong>
                </p>
                <p>
                  Le présent document autorise le porteur à suivre le traitement médical dans son centre de santé affilié à MUTU NDE ESPOIR ASBL.
                </p>
                <p>
                  Cette autorisation est valable pour toutes les prestations médicales couvertes par la mutuelle, conformément aux conditions générales d'adhésion.
                </p>
              </div>
            </div>

            {/* Enfants du Membre */}
            {type === 'membre' && personne.enfants && personne.enfants.length > 0 && (
              <div className="bon-traitement-section">
                <h3>ENFANTS DU MEMBRE</h3>
                
                <div className="enfants-membre-table-container">
                  <table className="enfants-membre-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Photo</th>
                        <th>Nom</th>
                        <th>Prénom</th>
                        <th>Date de Naissance</th>
                        <th>Lien</th>
                      </tr>
                    </thead>
                    <tbody>
                      {personne.enfants.map((enfant, index) => (
                        <tr key={enfant.id || index} className={index % 2 === 0 ? 'enfant-row-even' : 'enfant-row-odd'}>
                          <td>{index + 1}</td>
                          <td>
                            {enfant.photo ? (
                              <img 
                                src={`${API_BASE_URL}${enfant.photo}`} 
                                alt={`${enfant.nom} ${enfant.prenom}`}
                                style={{ 
                                  width: '50px', 
                                  height: '50px', 
                                  objectFit: 'cover', 
                                  borderRadius: '4px',
                                  border: '1px solid #ddd'
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  if (e.target.nextSibling) {
                                    e.target.nextSibling.style.display = 'inline';
                                  }
                                }}
                              />
                            ) : null}
                            {!enfant.photo && (
                              <span style={{ color: '#999', fontSize: '10px' }}>Pas de photo</span>
                            )}
                          </td>
                          <td>{enfant.nom || '-'}</td>
                          <td>{enfant.prenom || '-'}</td>
                          <td>{formatDateShort(enfant.date_naissance) || '-'}</td>
                          <td>{enfant.lien || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Membres de la Famille */}
            {type === 'famille' && personne.membres && personne.membres.length > 0 && (
              <div className="bon-traitement-section">
                <h3>MEMBRES DE LA FAMILLE</h3>
                
                <div className="membres-famille-table-container">
                  <table className="membres-famille-table">
                    <thead>
                      <tr>
                        <th>#</th>
                           <th>Photo</th>
                        <th>Nom</th>
                        <th>Prénom</th>
                        <th>Date de Naissance</th>
                        <th>Lien de Parenté</th>
                      </tr>
                    </thead>
                    <tbody>
                      {personne.membres.map((membre, index) => (
                        <tr key={membre.id || index} className={index % 2 === 0 ? 'membre-row-even' : 'membre-row-odd'}>
                          <td>{index + 1}</td>
                          <td>
                            {membre.photo ? (
                              <img
                                src={membre.photo}
                                alt={`Photo de ${membre.prenom || ''} ${membre.nom || ''}`}
                                style={{
                                  width: '50px',
                                  height: '50px',
                                  objectFit: 'cover',
                                  borderRadius: '4px',
                                  border: '1px solid #ddd'
                                }}
                                onError={e => {
                                  e.target.style.display = 'none';
                                  if (e.target.nextSibling) {
                                    e.target.nextSibling.style.display = 'inline';
                                  }
                                }}
                              />
                            ) : (
                              <span style={{ color: '#999', fontSize: '10px' }}>Pas de photo</span>
                            )}
                          </td>
                          <td>{membre.nom || '-'}</td>
                          <td>{membre.prenom || '-'}</td>
                          <td>{formatDateShort(membre.date_naissance) || '-'}</td>
                          <td>{membre.lien || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Évolution des Cotisations */}
            {cotisationsGrouped.length > 0 && (
              <div className="bon-traitement-section">
                <h3>ÉVOLUTION DES COTISATIONS</h3>
                
                {/* Tableau des cotisations */}
                <div className="cotisations-table-container">
                  <table className="cotisations-table">
                    <thead>
                      <tr>
                        <th>Année</th>
                        <th>Mois</th>
                        {hasSemaines ? (
                          <>
                            <th>Semaine 1</th>
                            <th>Semaine 2</th>
                            <th>Semaine 3</th>
                            <th>Semaine 4</th>
                          </>
                        ) : (
                          <>
                            <th>Statut</th>
                            <th>Date Échéance</th>
                            <th>Date Paiement</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {cotisationsGrouped.map((groupe, index) => {
                        if (hasSemaines) {
                          // Affichage pour les membres (avec semaines)
                          const semaines = [1, 2, 3, 4].map(semaine => groupe.semaines[semaine] || null);
                          const hasAnyCotisation = semaines.some(s => s !== null);
                          
                          if (!hasAnyCotisation) return null;
                          
                          return (
                            <tr key={`${groupe.annee}-${groupe.mois}`} className={index % 2 === 0 ? 'cotisation-row-even' : 'cotisation-row-odd'}>
                              <td>{groupe.annee || '-'}</td>
                              <td>{getMonthName(groupe.mois) || '-'}</td>
                              {semaines.map((cotisation, semaineIndex) => (
                                <td key={semaineIndex} className="semaine-cell">
                                  {cotisation ? (
                                    <div className="semaine-content">
                                      <span className={`statut-badge ${cotisation.statut === 'payee' || cotisation.statut === 'paye' ? 'statut-payee' : 'statut-impayee'}`}>
                                        {cotisation.statut === 'payee' || cotisation.statut === 'paye' ? '✅' : '❌'}
                                      </span>
                                      <div className="semaine-details">
                                        <div className="semaine-date">{formatDateShort(cotisation.date_echeance)}</div>
                                        {cotisation.date_paiement && (
                                          <div className="semaine-paiement">{formatDateShort(cotisation.date_paiement)}</div>
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="semaine-empty">-</span>
                                  )}
                                </td>
                              ))}
                            </tr>
                          );
                        } else {
                          // Affichage pour les familles (mensuel, pas de semaines)
                          const cotisation = groupe.cotisation;
                          return (
                            <tr key={`${groupe.annee}-${groupe.mois}`} className={index % 2 === 0 ? 'cotisation-row-even' : 'cotisation-row-odd'}>
                              <td>{groupe.annee || '-'}</td>
                              <td>{getMonthName(groupe.mois) || '-'}</td>
                              <td>
                                <span className={`statut-badge ${cotisation.statut === 'payee' || cotisation.statut === 'paye' ? 'statut-payee' : 'statut-impayee'}`}>
                                  {cotisation.statut === 'payee' || cotisation.statut === 'paye' ? '✅ Payée' : '❌ Impayée'}
                                </span>
                              </td>
                              <td>{formatDateShort(cotisation.date_echeance)}</td>
                              <td>{cotisation.date_paiement ? formatDateShort(cotisation.date_paiement) : '-'}</td>
                            </tr>
                          );
                        }
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Pied de page */}
            <div className="bon-traitement-footer">
              <div className="bon-traitement-signature">
                <div className="signature-line">
                  <p>Signature du Responsable</p>
                  <div className="signature-space"></div>
                </div>
              </div>
              <div className="bon-traitement-date">
                <p>Date: {new Date().toLocaleDateString('fr-FR')}</p>
              </div>
            </div>
            
            {/* Informations de contact */}
            <div className="bon-traitement-contact-info">
              <p>MUTU NDE ESPOIR ASBL - +243 846 273 669 - 860 791 529 - Plateau Boyoma, en face de bloc Universitaire (Réf : Bureau REGIDESO)</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BonTraitement;
